import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Info } from "lucide-react";

const PrivacyPolicyPage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <span>Privacy Policy</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              Privacy Policy
            </h1>
            <p className="text-lg mb-3" style={{ color: "#4A5568" }}>
              How we collect, use, and protect your information
            </p>
            <p className="text-sm italic" style={{ color: "#6B7280" }}>
              Last updated: February 4, 2025
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-5 bg-white">
          <div className="max-w-4xl mx-auto">
            
            {/* Plain Language Summary */}
            <div className="rounded-lg p-8 mb-16 flex gap-4" style={{ backgroundColor: "#DBEAFE", borderLeft: "4px solid #3B82F6" }} data-testid="summary-box">
              <Info size={32} style={{ color: "#3B82F6", flexShrink: 0 }} />
              <div>
                <h2 className="text-xl font-bold mb-3" style={{ color: "#1F2937" }}>In Plain Language:</h2>
                <p className="text-base leading-relaxed" style={{ color: "#1E40AF", lineHeight: "1.7" }}>
                  We collect only the information necessary to provide our services and respond to your inquiries. We protect your data with strong security measures and never sell your information to third parties. You can request to see, correct, or delete your information at any time.
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <PolicySection title="1. Introduction">
              <p>
                A3 Technologies Inc. ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
              </p>
            </PolicySection>

            {/* Section 2 */}
            <PolicySection title="2. Information We Collect">
              <h3 className="text-xl font-bold mt-6 mb-4" style={{ color: "#1F2937" }}>Personal Information:</h3>
              <ul className="list-disc ml-6 space-y-2" style={{ color: "#4A5568" }}>
                <li>Name and contact information</li>
                <li>Business information</li>
                <li>Email addresses</li>
                <li>Phone numbers</li>
                <li>Payment information (when applicable)</li>
              </ul>
              
              <h3 className="text-xl font-bold mt-8 mb-4" style={{ color: "#1F2937" }}>Technical Information:</h3>
              <ul className="list-disc ml-6 space-y-2" style={{ color: "#4A5568" }}>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Website usage data</li>
                <li>Cookies and similar technologies</li>
              </ul>
            </PolicySection>

            {/* Section 3 */}
            <PolicySection title="3. How We Use Your Information">
              <p>We use the information we collect in the following ways:</p>
              <ul className="list-disc ml-6 space-y-2 mt-4" style={{ color: "#4A5568" }}>
                <li>To provide and maintain our services</li>
                <li>To respond to your inquiries and requests</li>
                <li>To send administrative information</li>
                <li>To improve our website and services</li>
                <li>To send marketing communications (with your consent)</li>
                <li>To comply with legal obligations</li>
                <li>To detect and prevent fraud or security issues</li>
              </ul>
            </PolicySection>

            {/* Section 4 */}
            <PolicySection title="4. How We Share Your Information">
              <p>
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc ml-6 space-y-2 mt-4" style={{ color: "#4A5568" }}>
                <li><strong>Service Providers:</strong> We may share information with third-party vendors who assist our operations (e.g., email services, payment processors, cloud hosting)</li>
                <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or government regulations</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred</li>
                <li><strong>With Your Consent:</strong> We may share information for other purposes with your explicit consent</li>
              </ul>
            </PolicySection>

            {/* Section 5 */}
            <PolicySection title="5. Data Security">
              <p>
                We implement appropriate technical and organizational security measures to protect your information from unauthorized access, disclosure, alteration, and destruction. These measures include:
              </p>
              <ul className="list-disc ml-6 space-y-2 mt-4" style={{ color: "#4A5568" }}>
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Secure servers protected by firewalls</li>
                <li>Regular security assessments and updates</li>
                <li>Limited employee access to personal information</li>
                <li>Secure authentication methods</li>
              </ul>
              <p className="mt-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </PolicySection>

            {/* Section 6 */}
            <PolicySection title="6. Your Rights and Choices">
              <p>You have the following rights regarding your personal information:</p>
              <ul className="list-disc ml-6 space-y-2 mt-4" style={{ color: "#4A5568" }}>
                <li><strong>Access:</strong> Request copies of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your information</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data Portability:</strong> Request transfer of your data</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us using the information provided at the end of this policy.
              </p>
            </PolicySection>

            {/* Section 7 */}
            <PolicySection title="7. Cookies and Tracking Technologies">
              <p>
                We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies help us:
              </p>
              <ul className="list-disc ml-6 space-y-2 mt-4" style={{ color: "#4A5568" }}>
                <li>Remember your preferences</li>
                <li>Analyze website traffic and usage</li>
                <li>Improve user experience</li>
                <li>Provide relevant content</li>
              </ul>
              <p className="mt-4">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our website.
              </p>
              <p className="mt-4">
                For more information about cookies and how to manage them, visit{" "}
                <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  www.allaboutcookies.org
                </a>
              </p>
            </PolicySection>

            {/* Section 8 */}
            <PolicySection title="8. Third-Party Websites">
              <p>
                Our website may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
              </p>
              <p className="mt-4">
                We strongly advise you to review the privacy policy of every site you visit.
              </p>
            </PolicySection>

            {/* Section 9 */}
            <PolicySection title="9. Children's Privacy">
              <p>
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
              </p>
            </PolicySection>

            {/* Section 10 */}
            <PolicySection title="10. Data Retention">
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law.
              </p>
              <p className="mt-4">
                When we no longer need your information, we will securely delete or anonymize it.
              </p>
            </PolicySection>

            {/* Section 11 */}
            <PolicySection title="11. Changes to This Privacy Policy">
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top.
              </p>
              <p className="mt-4">
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </PolicySection>

            {/* Section 12 */}
            <PolicySection title="12. Contact Us">
              <p>
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="mt-6 p-8 rounded-lg border-2 border-gray-200" style={{ backgroundColor: "#F9FAFB" }}>
                <p className="font-bold text-lg mb-4" style={{ color: "#1F2937" }}>A3 Technologies Inc.</p>
                <p className="mb-2">
                  <strong>Email:</strong>{" "}
                  <a href="mailto:privacy@a3technologies.com" className="text-blue-600 hover:underline">
                    privacy@a3technologies.com
                  </a>
                </p>
                <p className="mb-4">
                  <strong>Phone:</strong>{" "}
                  <a href="tel:7783497449" className="text-blue-600 hover:underline">
                    (778) 349-7449
                  </a>
                </p>
                <p className="text-sm" style={{ color: "#6B7280" }}>
                  British Columbia, Canada
                </p>
              </div>
            </PolicySection>

            {/* PIPEDA Compliance Note */}
            <div className="rounded-lg p-6 mt-12" style={{ backgroundColor: "#D1FAE5", borderLeft: "4px solid #2C5F2D" }}>
              <h3 className="text-lg font-bold mb-3" style={{ color: "#065F46" }}>PIPEDA Compliance</h3>
              <p className="text-base" style={{ color: "#065F46", lineHeight: "1.7" }}>
                A3 Technologies Inc. is committed to compliance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation in Canada.
              </p>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: "#1F2937" }}>
              Questions About Your Privacy?
            </h2>
            <p className="text-base mb-8" style={{ color: "#4A5568" }}>
              We're here to help. Contact us with any privacy concerns.
            </p>
            <Link to="/contact">
              <Button
                data-testid="contact-btn"
                className="text-lg font-semibold px-8 py-6 rounded-md"
                style={{ backgroundColor: "#3B82F6" }}
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Helper Component
const PolicySection = ({ title, children }) => (
  <div className="mb-12" style={{ color: "#374151", lineHeight: "1.8" }}>
    <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: "#1F2937" }}>{title}</h2>
    <div className="text-base space-y-4">{children}</div>
  </div>
);

export default PrivacyPolicyPage;
